/**
 * Ends the session and returns to the marketing site.
 *
 * The local session is cleared even when the request fails. A button that
 * appears to do nothing is worse than one that logs you out optimistically:
 * the cookie is the server's to revoke and it will expire on its own, whereas
 * a user left staring at their own account after asking to leave — on a shared
 * machine, say — has no other move.
 */

import { useState } from "react";
import { useNavigate } from "react-router-dom";

import { useSession } from "./AuthProvider";

interface Props {
  /** Defaults to the standalone-button look; the profile menu passes its own
   * menu-item class instead. */
  className?: string;
  /** Set to "menuitem" when this renders inside a `role="menu"` dropdown. */
  role?: string;
  /** Fired synchronously on click, before the sign-out request starts — e.g.
   * so a dropdown this button lives in closes immediately instead of sitting
   * open over the page for the duration of the request. */
  onClick?: () => void;
}

export function SignOutButton({ className = "button button--small", role, onClick }: Props = {}) {
  const { api, clear } = useSession();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);

  async function signOut() {
    if (busy) return;
    setBusy(true);
    onClick?.();

    // Leave the gated route before forgetting the session, and let the request
    // sit between the two. The order is the whole point: while the user is
    // still on `/dashboard`, clearing them is what the gate is watching for,
    // and it redirects to the login form — so asking to sign out lands on a
    // form inviting you back in. Awaiting in between guarantees the
    // navigation has committed and `/dashboard` is unmounted before the
    // session goes.
    navigate("/", { replace: true });

    try {
      await api.signOut();
    } catch {
      // Nothing to be done about a cookie only the server can revoke, and
      // leaving someone signed in on a shared machine because the network
      // blipped is the worse of the two failures — so `finally` clears anyway.
    } finally {
      clear();
    }
  }

  return (
    <button type="button" className={className} role={role} onClick={signOut} disabled={busy}>
      {busy ? "Signing out…" : "Sign out"}
    </button>
  );
}
